/** libs */
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { ReactSVG } from 'react-svg';
import { LegacyRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import parse, { Element, HTMLReactParserOptions } from 'html-react-parser';
import { format } from 'date-fns';
import * as Sentry from '@sentry/nextjs';

/** components */
import Breadcrumbs from '../../shared/miscellaneous/breadcrumbs/breadcrumbs';
import TableNavbar from '@components/blog/table-navbar/table-navbar';
import FormIncut from '@components/shared/miscellaneous/incuts-types/form-incut/form-incut';
import LinkIncut from '@components/shared/miscellaneous/incuts-types/incut-link/incut-link';
import ArticlePreviewMedium from '../article-previews/article-preview-medium/article-preview-medium';

/** utils and models */
import { getVisitorSecret } from '@libs/utils/visitor.utils';
import { IArticle, IArticleRating } from '@models/article.interface';
import { getArticleRating, setArticleRating } from '@services/article.service';
import {
  articleAuthor,
  getImagesSourceFromHTML,
  imageSrcReplacer,
  isInternalArticleLink,
} from '@libs/utils/article.utils';
import { IArticleRatingStructureData, IArticleStructureData } from '@components/blog/article-page/article-page.data';

/** configs */
import { config } from '../../../config';

/** styles */
import style from './article-page.module.scss';
import { replaceDeprecatedIncuts, updateIncutsImage } from '@libs/utils/incut.utils';
import FAQSection from '@components/shared/blocks/faq';
import { IFAQItem } from '@components/shared/blocks/faq/types';
import ShouldRender from '@components/shared/boundaries/should-render';
import ReadingTimeBlock from '../reading-time-block/reading-time';
import Link from 'next/link';
import { restoreScrollBehavior, setScrollBehaviorAuto } from '@helpers/smooth-scroll.helper';
import ArticleSubscription from '../article-subscription';
import { WindowSizeBreakpointsEnum } from '@helpers/window-size-breakpoints';
import arrowIcon from '/public/assets/icons/icn-arrow.svg';

const ShareButtons = dynamic(() => import('../../shared/miscellaneous/share-buttons/share-buttons'));
const ArticleFormSubscription = dynamic(() => import('../article-form-subscription/article-form-subscription'));
const GetInTouchComponent = dynamic(() => import('../../shared/interactions/get-in-touch/get-in-touch'));

interface ArticlePageProps {
  article: IArticle;
  articleRating: IArticleRating;
}

interface IRatingResponse {
  user: {
    rating: number;
  };
  article: {
    count: number;
    average: string;
  };
}

const ArticlePage = ({ article, articleRating }: ArticlePageProps) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [ratingCount, setRatingCount] = useState(articleRating.count);
  const [averageRating, setAverageRating] = useState(articleRating.average);
  const relatedArticles = article.relatedArticles.slice(0, 3).map((ra: any) => ra.childArticle);
  const [titles, setTitles] = useState<string[]>([]);
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const [isDesktop, setIsDesktop] = useState(false);

  const { asPath } = useRouter();
  const titlesObserver = useRef<IntersectionObserver>();
  const faqTitleRef = useRef<HTMLHeadingElement>();

  const author = articleAuthor(article);
  const [firstName, lastName] = author.name.split(' ');
  const authorAbbreviation = `${firstName.charAt(0)}. ${lastName}`;

  const content = imageSrcReplacer(article.content);

  const scrollbarRef = useRef<HTMLDivElement | null>(null);
  const scrollContent = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const MAX_WIDTH_ELEMENT = 100;
  const CLASS_INCUT_ITEM = 'incut-item';
  const [isChapterScroll, setIsChapterScroll] = useState<boolean>(true);
  const selectedTitleSelector = '[data-selected-title="true"]';

  const [isTocOpen, setTocOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const NEWS_LABEL = 'News';

  useEffect(() => {
    const calculateScrollbarWidth = () => {
      if (!scrollContent.current || !scrollbarRef.current) return;

      const scrollPosition = window.innerHeight - scrollContent.current.getBoundingClientRect().top;

      const totalHeight = scrollContent.current.scrollHeight - window.innerHeight;
      const width = (scrollPosition / totalHeight) * MAX_WIDTH_ELEMENT;

      scrollbarRef.current.style.width = `${width}%`;
    };

    window.addEventListener('scroll', calculateScrollbarWidth);

    return () => {
      window.removeEventListener('scroll', calculateScrollbarWidth);
    };
  }, []);

  useEffect(() => {
    setScrollBehaviorAuto();
    return () => {
      restoreScrollBehavior();
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= WindowSizeBreakpointsEnum.Tablet);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      window.scroll({
        top: 0,
        left: 0,
      });
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element && domNode.attribs) {
        if (domNode.attribs.class === 'form-incut') {
          const title = domNode.attribs['data-title'];
          const text = domNode.attribs['data-text'];
          const buttonText = domNode.attribs['data-button-text'];
          return <FormIncut title={title} text={text} buttonText={buttonText} articleId={article.id} />;
        }
        if (domNode.attribs.class === CLASS_INCUT_ITEM) {
          const link = domNode.attribs['data-link'];
          const text = domNode.attribs['data-text'];
          return <LinkIncut text={text} link={link} />;
        }
        if (domNode.attribs.href && isInternalArticleLink(domNode.attribs.href)) {
          delete domNode.attribs.rel;
          return domNode;
        }
      }
    },
  };
  const faq: IFAQItem[] = article.faq.map((item) => ({
    question: item.question,
    answers: [item.answer],
  }));

  const toggleToc = () => setTocOpen((prev) => !prev);

  useEffect(() => {
    getArticleRating(article.id, getVisitorSecret())
      .then((rating: IRatingResponse) => {
        updateRating(rating);
      })
      .catch((error) => {
        // rating is non-critical for the page; log for visibility but don't surface to the user
        Sentry.captureException(error, {
          tags: { source: 'getArticleRating', type: error?.type, online: error?.cause?.online },
          extra: { articleId: article.id, ...error?.cause },
        });
      });

    setSelectedTitle(() => '');
    updateArticleTitles(article.content);
  }, [asPath]);

  useEffect(() => {
    const titles: HTMLHeadingElement[] = Array.from(document.querySelectorAll('h2'));
    let isFirstCall: boolean = true;
    if (isChapterScroll) {
      const handleObserver = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
          if (entry.isIntersecting) {
            const firstIntersectingEntry = entries.find((entry) => entry.isIntersecting);
            if (firstIntersectingEntry) {
              if (firstIntersectingEntry.target.textContent !== null) {
                setSelectedTitle(firstIntersectingEntry.target.textContent);
                if (isDesktop) {
                  const elem = firstIntersectingEntry.target as HTMLElement;
                  const container = containerRef.current;
                  if (!container) return;

                  const isFirstElement = elem === titles[0];
                  const isLastElement = elem === titles[titles.length - 1];
                  if (isFirstElement || isLastElement) return;

                  const containerRect = container.getBoundingClientRect();
                  const elemRect = elem.getBoundingClientRect();

                  const offset = 10;

                  let scrollAmount = 0;

                  if (elemRect.top < containerRect.top + offset) {
                    scrollAmount = elemRect.top - containerRect.top - offset;
                  } else if (elemRect.bottom > containerRect.bottom - offset) {
                    scrollAmount = elemRect.bottom - containerRect.bottom + offset;
                  }

                  if (scrollAmount !== 0) {
                    const maxScroll = elemRect.height;
                    const finalScroll =
                      Math.abs(scrollAmount) > maxScroll ? (scrollAmount > 0 ? maxScroll : -maxScroll) : scrollAmount;
                    container.scrollBy({ top: finalScroll, behavior: 'smooth' });
                  }
                }
              }
            }
          }

          if (!entry.isIntersecting && !isFirstCall) {
            const selected = document.querySelector(selectedTitleSelector);
            const disappearedBelow: boolean = entry.target.getBoundingClientRect().top > 0;
            const stillActive: boolean = entry.target.textContent === selected?.textContent;

            if (disappearedBelow && stillActive) {
              const previousTitleIdx: number =
                titles.findIndex(({ innerText }) => innerText === selected!.textContent) - 1;

              previousTitleIdx >= 0 && setSelectedTitle(() => titles[previousTitleIdx].innerText);
            }
          }
        });
        if (isFirstCall) {
          isFirstCall = false;
        }
      };

      titlesObserver.current = new IntersectionObserver(handleObserver, {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: 1,
      });

      titles.forEach((elem) => titlesObserver.current?.observe(elem));

      return () => {
        titlesObserver.current?.disconnect();
      };
    }
  }, [asPath, isChapterScroll]);

  useLayoutEffect(() => {
    updateIncutsImage();
    replaceDeprecatedIncuts();
  }, []);

  function formatDate(date: Date): string {
    const dateFormat = 'dd MMM yyyy';
    return format(Date.parse(String(date)), dateFormat);
  }

  function saveRating(newRating: number): void {
    setArticleRating(article.id, getVisitorSecret(), newRating)
      .then((ratingResponse: IRatingResponse) => {
        updateRating(ratingResponse);
      })
      .catch((error) => {
        Sentry.captureException(error, {
          tags: { source: 'setArticleRating', type: error?.type, online: error?.cause?.online },
          extra: { articleId: article.id, newRating, ...error?.cause },
        });
      });
  }

  function updateRating(rating: IRatingResponse): void {
    setRatingCount(rating.article.count);
    setAverageRating(rating.article.average);
    setRating(rating.user.rating);
    setHover(rating.user.rating || 0);
  }

  function updateArticleTitles(content: string): void {
    const html = document.createElement('div');
    html.innerHTML = content;
    const titles = Array.from(html.querySelectorAll('h2')).map((t) => t.innerText);
    faqTitleRef.current?.innerText && titles.push(faqTitleRef.current?.innerText);
    setTitles(() => titles);
    html.remove();
  }

  function scrollToChapter(title: string): void {
    setIsChapterScroll(false);
    const h2Tags = Array.from(document.querySelectorAll('h2'));
    const selectedTitle = h2Tags.find(({ innerText }) => innerText === title);
    if (selectedTitle) {
      const headerHeight = 80;
      const targetOffset = selectedTitle.getBoundingClientRect().top;
      const scrollToPosition = window.scrollY + targetOffset - headerHeight;

      const animateScroll = () => {
        const currentTime = Date.now();
        const elapsed = Math.min(1, (currentTime - startTime) / duration);
        const ease = easeInOutQuad(elapsed);
        scrollTo(startingY + distance * ease, () => {});
        if (currentTime < startTime + duration) {
          requestAnimationFrame(animateScroll);
        }
      };

      const duration = 10;
      const startTime = Date.now();
      const startingY = window.scrollY;
      const distance = scrollToPosition - startingY;

      requestAnimationFrame(animateScroll);
      setSelectedTitle(() => selectedTitle.innerText);
    }
  }

  const scrollTo = (offset: number, callback: { (): void }) => {
    const onScroll = () => {
      if (Math.abs(window.scrollY - offset) <= 10) {
        window.removeEventListener('scroll', onScroll);
        setIsChapterScroll(true);
        callback();
      }
    };

    window.addEventListener('scroll', onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: 'smooth',
    });
  };

  function easeInOutQuad(t: number): number {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  const articleRatingStructureData: IArticleRatingStructureData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWorkSeries',
    name: article.header,
    aggregateRating: {
      '@type': 'AggregateRating',
      worstRating: articleRating.minRating,
      ratingValue: Number(articleRating.average),
      bestRating: articleRating.maxRating,
      reviewCount: Number(articleRating.count),
    },
  };

  const articleStructureData: IArticleStructureData = {
    '@context': 'https://schema.org',
    '@type': article.label === NEWS_LABEL ? 'NewsArticle' : 'BlogPosting',
    mainEntityOfPage: `${config.HOST}${asPath}`,
    headline: article.header,
    datePublished: article.createdAt,
    dateModified: article.contentUpdatedAt,
    description: article.metaDescription,
    author: {
      '@type': 'Person',
      name: author.name,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Agilie',
      logo: {
        '@type': 'ImageObject',
        url: 'https://agilie.com/_next/image?url=/assets/header/logo/agilie-logo.svg&w=96&q=75',
        width: 88,
        height: 24,
      },
    },
    image: getImagesSourceFromHTML(article.content),
  };

  return (
    <>
      <div ref={scrollbarRef} className={style.scroll__bar}></div>
      {[articleRatingStructureData, articleStructureData].map((structureData) => (
        <script
          key={structureData['@type']}
          type={'application/ld+json'}
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structureData) }}
        />
      ))}
      <div className={style.container}>
        <div className={style.article__header}>
          <Breadcrumbs lastElementTitle={article.header} />
          <div className={style.article__metadata}>
            <div className={style.article__metadata__info}>
              <h1 className={style.article__title}>{article.header}</h1>
              <div className={style.article__metadata__info__wrapper}>
                <div className={style.article__metadata__info__wrapper__item}>{article.label}</div>
                <div className={style.article__metadata__info__wrapper__item}>{article.readingTime} min read</div>
                <span className={style.article__metadata__info__wrapper__item}>
                  {formatDate(article.contentUpdatedAt)}
                </span>
              </div>
              <Link scroll={false} href={`/blog/authors/${author.slug}`}>
                <a className={style.article__author__link}>
                  <div className={style.article__author}>
                    <div className={style.article__author_image}>
                      <Image src={author.avatarUrl} alt={author.name} layout={'fill'} objectFit={'contain'} />
                    </div>
                    <div className={style.article__author__info}>
                      <span className={style.article__author__name}>By {author.name}</span>
                      <div className={style.article__timestamps}>
                        <div className={style.article__updated}>{author.position}</div>
                      </div>
                    </div>
                  </div>
                </a>
              </Link>
            </div>
            <div className={style.article__metadata__wrap}>
              <Image
                className={style.article__metadata__wrap__img}
                sizes="1024px"
                layout={'fill'}
                src={article.posterUrl}
                alt={article.header}
              />
            </div>
          </div>
        </div>
        <section className={style.article}>
          <div className={`${style.article__share} ${style.desktop}`}>
            <ShareButtons />
          </div>
          <div ref={scrollContent} className={style.article__content}>
            <div className={style.article__main}>{parse(content, options)}</div>
            <ShouldRender if={faq.length} else={<div className={style.divider}></div>}>
              <FAQSection
                titleRef={faqTitleRef as LegacyRef<HTMLHeadingElement>}
                title="FAQ"
                itemsFAQ={faq}
                hoverColor="grey"
              />
            </ShouldRender>
            <div className={style.article__rate}>
              <span className={style.article__rate_title}>Rate this article</span>
              <div className={`${style.article__rate_main}`}>
                {[...Array(articleRating.maxRating)].map((star, index) => {
                  index++;
                  const isFulfilled: boolean = index <= (hover || rating);
                  return (
                    <ReactSVG
                      key={index}
                      className={`${style.article__rate_star} ${isFulfilled ? style.fulfilled : ''}`}
                      src={'/assets/icons/star.svg'}
                      onClick={() => saveRating(index)}
                      onMouseEnter={() => setHover(index)}
                      onMouseLeave={() => setHover(rating)}
                    />
                  );
                })}
              </div>
              <div className={style.article__rate_summary}>
                {ratingCount} ratings, average {averageRating} of out {articleRating.maxRating}
              </div>
            </div>
            <div className={`${style.divider} ${style.mb0}`}></div>
          </div>
          <div>
            <div className={style.article__sidebar}>
              {isDesktop && article.isNeedForm ? <ArticleFormSubscription /> : <ArticleSubscription />}
              <div className={`${style.article__navigation}`}>
                <div className={style.article__navigation_title} onClick={toggleToc}>
                  Table of contents
                  {!isDesktop && (
                    <Image
                      src={arrowIcon}
                      alt="arrow"
                      className={`${style.arrow} ${isTocOpen ? style.open : ''}`}
                      width={20}
                      height={20}
                    />
                  )}
                </div>
                {(isDesktop || isTocOpen) && titles.length > 0 && (
                  <div ref={containerRef} className={style.article__navigation__list}>
                    <TableNavbar titles={titles} activeTitle={selectedTitle} handler={scrollToChapter} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
        <section className={style.form}>
          <GetInTouchComponent withSubtitle={false} />
        </section>
        <section className={style.recommendations}>
          <div className={style.divider}></div>
          <span className={style.recommendations__title}>Related articles</span>

          <div className={style.recommendations__items}>
            {relatedArticles.map((item) => {
              return <ArticlePreviewMedium key={item.id} article={item} />;
            })}
          </div>
          <div className={style.recommendations__items__for__tablet}>
            {relatedArticles.slice(0, 2).map((item) => {
              return <ArticlePreviewMedium key={item.id} article={item} />;
            })}
          </div>
        </section>
        <div className={`${style.article__share_mobile}`}>
          <ShareButtons />
        </div>
      </div>
    </>
  );
};

export default ArticlePage;
